import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import { IOfficeChatMessage } from '@/store/store.officeChat';

import { http } from './http';

interface PollResponse {
  messages: IOfficeChatMessage[];
  hasMore: boolean;
  timeout?: boolean;
  pinnedMessage?: IOfficeChatMessage | null;
  deletedMessageIds?: string[];
}

interface MessagesResponse {
  messages: IOfficeChatMessage[];
  hasMore: boolean;
  pinnedMessage?: IOfficeChatMessage | null;
}

interface MessageResponse {
  message: IOfficeChatMessage;
}

export const OfficeChatApi = {
  poll: async (
    lastMessageId?: string | null,
    timeout?: number,
    lastPollTime?: number,
  ): Promise<AxiosResponse<PollResponse>> => {
    return http.get(`api/${ApiVersion.V1}/office-chat/poll`, {
      params: {
        ...(lastMessageId && { lastMessageId }),
        ...(timeout && { timeout }),
        ...(lastPollTime && { lastPollTime }),
      },
    });
  },

  getMessages: async (
    limit?: number,
    before?: string,
  ): Promise<AxiosResponse<MessagesResponse>> => {
    return http.get(`api/${ApiVersion.V1}/office-chat/messages`, {
      params: {
        ...(limit && { limit }),
        ...(before && { before }),
      },
    });
  },

  sendMessage: async (
    message: string,
  ): Promise<AxiosResponse<MessageResponse>> => {
    return http.post(`api/${ApiVersion.V1}/office-chat/messages`, { message });
  },

  editMessage: async (
    messageId: string,
    message: string,
  ): Promise<AxiosResponse<MessageResponse>> => {
    return http.patch(
      `api/${ApiVersion.V1}/office-chat/messages/${messageId}`,
      {
        message,
      },
    );
  },

  deleteMessage: async (
    messageId: string,
  ): Promise<AxiosResponse<{ success: boolean }>> => {
    return http.delete(
      `api/${ApiVersion.V1}/office-chat/messages/${messageId}`,
    );
  },

  pinMessage: async (
    messageId: string,
  ): Promise<AxiosResponse<MessageResponse>> => {
    return http.post(
      `api/${ApiVersion.V1}/office-chat/messages/${messageId}/pin`,
    );
  },

  unpinMessage: async (
    messageId: string,
  ): Promise<AxiosResponse<MessageResponse>> => {
    return http.delete(
      `api/${ApiVersion.V1}/office-chat/messages/${messageId}/pin`,
    );
  },

  markAsViewed: async (): Promise<AxiosResponse<void>> => {
    return http.post(`api/${ApiVersion.V1}/office-chat/mark-as-viewed`);
  },

  getUnreadCount: async (): Promise<AxiosResponse<{ count: number }>> => {
    return http.get(`api/${ApiVersion.V1}/office-chat/unread-count`);
  },
};
